import type { ComponentProps, Ref } from 'react';
import { ButtonControl, Tooltip } from './ButtonControl';

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from './Icon';

export type ListScrollProps = ComponentProps<'div'>;

export function ListScroll({ className, ...props }: ListScrollProps) {
  return <div className={cx('list-scroll', className)} {...props} />;
}

export type ListColumnProps = ComponentProps<'div'>;

export function ListColumn({ className, ...props }: ListColumnProps) {
  return <div className={cx('list-column', className)} {...props} />;
}

export type ListProps = ComponentProps<'div'>;

export function List({ className, ...props }: ListProps) {
  return <div className={cx('list', className)} {...props} />;
}

export interface ListToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  open: boolean;
  onToggle?: () => void;
  center?: boolean;
}

export function ListToggle(
  { ref, open, onToggle, center = false, className, type = 'button', onClick, ...rest }: ListToggleProps & { ref?: Ref<HTMLButtonElement> },
) {
  return (
    <ButtonControl
      ref={ref}
      type={type}
      className={cx('list-toggle', center && 'list-toggle--center', className)}
      aria-expanded={open}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onToggle?.();
      }}
      {...rest}
      aria-label={rest['aria-label'] ?? (rest['aria-labelledby'] ? undefined : rest.title)}
    >
      <Icon name={open ? 'i-chevron-down' : 'i-chevron-right'} />
    </ButtonControl>
  );
}

export interface ListRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  isSelected?: boolean;

  isClickable?: boolean;

  toggle?: ReactNode;

  title: ReactNode;
  props?: ReactNode;
  action?: ReactNode;
}

export function ListRow(
  { ref, isSelected = false,
    isClickable = false,
    toggle,
    title,
    props,
    action,
    className,
    ...rest
  }: ListRowProps & { ref?: Ref<HTMLDivElement> },
) {
  const row = (
    <div
      ref={ref}
      className={cx(
        'list-row',
        isClickable && 'is-clickable',
        isSelected && 'is-selected',
        className,
      )}
      tabIndex={isClickable && rest.onClick ? 0 : undefined}
      {...rest}
      onKeyDown={(event) => {
        rest.onKeyDown?.(event);
        if (!event.defaultPrevented && isClickable && rest.onClick && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          event.currentTarget.click();
        }
      }}
    >
      {toggle}
      <span className="list-row-title">{title}</span>
      {props != null && <span className="list-row-props">{props}</span>}
      {action}
    </div>
  );
  return isClickable && rest.onClick ? <Tooltip>{row}</Tooltip> : row;
}

export interface ListGroupHeaderProps extends HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  onToggle?: () => void;
  toggleLabel?: string;

  trailing?: ReactNode;

  children: ReactNode;
}

export function ListGroupHeader(
  { ref, open = false,
      onToggle,
      toggleLabel,
          trailing,
        className,
      children,
      ...rest
    }: ListGroupHeaderProps & { ref?: Ref<HTMLDivElement> },
) {
    const headerInteractive = rest.onClick != null;
    const header = (
      <div
        ref={ref}
        className={cx('list-group-header', className)}
        role={headerInteractive ? 'button' : undefined}
        tabIndex={headerInteractive ? 0 : undefined}
        {...rest}
        onKeyDown={(event) => {
          rest.onKeyDown?.(event);
          if (!event.defaultPrevented && headerInteractive && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      >
        {onToggle != null && (
          <ListToggle
            open={open}
            onToggle={onToggle}
            aria-label={toggleLabel}
            tabIndex={headerInteractive ? -1 : undefined}

            onClick={(event) => event.stopPropagation()}
          />
        )}
            <h3 className="section-header">{children}</h3>
        {trailing}
      </div>
    );
    return headerInteractive ? <Tooltip>{header}</Tooltip> : header;
  }

export type ListSublistProps = ComponentProps<'div'>;

export function ListSublist({ className, ...props }: ListSublistProps) {
  return <div className={cx('list-sublist', className)} {...props} />;
}

export interface ListRowActionProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: string;
  label: string;
}

export function ListRowAction(
  { ref, icon, label, className, type = 'button', ...rest }: ListRowActionProps & { ref?: Ref<HTMLButtonElement> },
) {
  return (
    <ButtonControl ref={ref} type={type} className={cx('list-row-action', className)} title={label} aria-label={label} {...rest}>
      <Icon name={icon} />
    </ButtonControl>
  );
}

export type ListEmptyProps = ComponentProps<'p'>;

export function ListEmpty({ className, ...props }: ListEmptyProps) {
  return <p className={cx('list-empty', className)} {...props} />;
}
